import React, { useState, useEffect } from "react"
import { useLocation } from "react-router-dom";

import { submitForm } from "../../api/axios";

const Form = ({ section }) => {
    const location = useLocation();
    const [values, setValues] = useState({
        name: "",
        email: "",
        phone: "",
        city: "",
        message: "",
    })

    const [errors, setErrors] = useState({
        name: "",
        email: "",
        phone: "",
        city: "",
        message: "",
    })

    const [touched, setTouched] = useState({
        name: false,
        email: false,
        phone: false,
        city: false,
        message: false,
    })

    const [submitting, setSubmitting] = useState(false)
    const [success, setSuccess] = useState(false)
    const [error, setError] = useState(null)

    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                setSuccess(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [success]);

    // Validation patterns
    const patterns = {
        name: /^[A-Za-z\s.]+$/,
        email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,

        // Phone: Accepts numbers, + at start, and dashes
        // Validates that after removing non-digits, we have 10-14 digits
        phone: /^[\d\s\-\(\)\.\+]{10,20}$/,

        city: /^[A-Za-z\s]+$/,
        message: /^.+$/,
    }

    // Validation messages
    const validationMessages = {
        name: "Name should contain only letters, spaces, and dots",
        email: "Please enter a valid email address",
        phone: "Please enter a valid mobile number",
        city: "City should contain only letters and spaces",
        message: "Message cannot be empty",
    }

    // Validate individual field
    const validateField = (name, value) => {
        let error = ""

        if (!value.trim()) {
            error = "This field is required"
            return error
        }

        switch (name) {
            case 'name':
                if (!patterns.name.test(value)) {
                    error = validationMessages.name
                }
                break
            case 'email':
                if (!patterns.email.test(value)) {
                    error = validationMessages.email
                }
                break
            case 'phone':
                // Remove all non-digit characters to count actual digits
                const phoneDigits = value.replace(/\D/g, '')

                // Check if we have 10-14 digits
                if (phoneDigits.length < 10 || phoneDigits.length > 14) {
                    error = validationMessages.phone
                }

                // Additional check: if + is present, it must be at the beginning
                if (value.includes('+') && !value.startsWith('+')) {
                    error = "Plus sign (+) must be at the beginning if used"
                }

                // Check for consecutive dashes or invalid dash placement
                if (value.includes('--')) {
                    error = "Invalid mobile number format"
                }
                break
            case 'city':
                if (!patterns.city.test(value)) {
                    error = validationMessages.city
                }
                break
            case 'message':
                if (!patterns.message.test(value)) {
                    error = validationMessages.message
                }
                break
            default:
                break
        }

        return error
    }

    // Validate all fields
    const validateAll = () => {
        const newErrors = {}
        let isValid = true

        Object.keys(values).forEach(key => {
            const error = validateField(key, values[key])
            if (error) {
                newErrors[key] = error
                isValid = false
            } else {
                newErrors[key] = ""
            }
        })

        setErrors(newErrors)
        return isValid
    }

    const handleChange = (e) => {
        const { name, value } = e.target
        let processedValue = value

        // Apply formatting/rules based on field
        switch (name) {
            case 'name':
                // Allow only letters, spaces, and dots
                processedValue = value.replace(/[^A-Za-z\s.]/g, '')
                break
            case 'phone':
                // Allow numbers, plus sign at beginning, and dashes
                // First, handle plus sign: only allow at beginning
                if (value.includes('+') && !value.startsWith('+')) {
                    // Remove any + signs that are not at the beginning
                    processedValue = value.replace(/\+/g, '')
                }

                // Allow only digits, plus at start, and dashes
                processedValue = processedValue.replace(/[^\d\+\-]/g, '')

                // Ensure only one plus sign at the beginning
                if (processedValue.includes('+')) {
                    const parts = processedValue.split('+')
                    if (parts.length > 2) {
                        // More than one plus sign, keep only the first one
                        processedValue = '+' + parts.slice(1).join('').replace(/\+/g, '')
                    }
                }

                // Remove consecutive dashes
                processedValue = processedValue.replace(/--+/g, '-')

                // Don't allow dash at the end
                if (processedValue.endsWith('-')) {
                    processedValue = processedValue.slice(0, -1)
                }

                // Limit total length (including + and -)
                if (processedValue.length > 20) {
                    processedValue = processedValue.substring(0, 20)
                }
                break
            case 'city':
                // Allow only letters and spaces
                processedValue = value.replace(/[^A-Za-z\s]/g, '')
                break
            default:
                break
        }

        setValues(prev => ({ ...prev, [name]: processedValue }))

        // Validate on change if field has been touched
        if (touched[name]) {
            const error = validateField(name, processedValue)
            setErrors(prev => ({ ...prev, [name]: error }))
        }
    }

    const handleBlur = (e) => {
        const { name, value } = e.target
        setTouched(prev => ({ ...prev, [name]: true }))

        // Validate on blur
        const error = validateField(name, value)
        setErrors(prev => ({ ...prev, [name]: error }))
    }

    // const formatPathName = (path) => {
    //     if (!path) return "";

    //     // Remove starting slash
    //     const clean = path.replace("/", "");

    //     // Replace hyphen with space
    //     const withSpaces = clean.replace(/-/g, " ");

    //     // Capitalize each word
    //     const formatted = withSpaces.replace(/\b\w/g, (char) =>
    //         char.toUpperCase()
    //     );

    //     return formatted;
    // };

    const formatPathName = (path) => {
    // 1. Handle root/Home path
    if (!path || path === "/") return "Home";

    // 2. Split path by "/" and filter out empty strings (e.g., from trailing slashes)
    const segments = path.split("/").filter(Boolean);
    
    // 3. Get the last segment
    const lastSegment = segments[segments.length - 1];

    // 4. Replace hyphens with spaces and capitalize
    const formatted = lastSegment
        .replace(/-/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());

    return formatted;
};


    const handleSubmit = async (e) => {
        e.preventDefault();

        setTouched({
            name: true,
            email: true,
            phone: true,
            city: true,
            message: true,
        });

        if (!validateAll()) {
            setError("Please fix the errors in the form");
            return;
        }

        setSubmitting(true);
        setError(null);
        setSuccess(false);

        // Clean phone number
        let cleanPhone = values.phone.startsWith('+')
            ? '+' + values.phone.substring(1).replace(/\D/g, '')
            : values.phone.replace(/\D/g, '');

        const payload = {
            name: values.name,
            email: values.email,
            phone: cleanPhone,
            city: values.city,
            message: values.message,
            page: location.pathname === "/" ? "Home" : formatPathName(location.pathname),
            pageUrl: location.pathname, // actual URL
            form_position: section, // top or bottom
        };


        try {
            await submitForm(payload);

            // ✅ Success
            setSuccess(true);

            setValues({
                name: "",
                email: "",
                phone: "",
                city: "",
                message: "",
            });

            setTouched({
                name: false,
                email: false,
                phone: false,
                city: false,
                message: false,
            });

        } catch (err) {
            console.error("Submission error:", err);
            setError("Submission failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };


    const hasError = (fieldName) => {
        return touched[fieldName] && errors[fieldName]
    }

    return (
        <>            
            <form id="contactForm" onSubmit={handleSubmit}>
                <div className="row">
                    {/* Name Field */}
                    <div className="col-lg-12 col-md-6">
                        <div className="form-group">
                            <input
                                type="text"
                                name="name"
                                className={`form-control ${hasError('name') ? 'is-invalid' : ''}`}
                                required
                                placeholder="Name"
                                value={values.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                maxLength="50"
                            />
                            {hasError('name') && (
                                <div className="invalid-feedback d-block">
                                    {errors.name}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Email Field */}
                    <div className="col-lg-12 col-md-6">
                        <div className="form-group">
                            <input
                                type="email"
                                name="email"
                                className={`form-control ${hasError('email') ? 'is-invalid' : ''}`}
                                required
                                placeholder="Email"
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                maxLength="100"
                            />
                            {hasError('email') && (
                                <div className="invalid-feedback d-block">
                                    {errors.email}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Phone Field */}
                    <div className="col-lg-6 col-md-12">
                        <div className="form-group">
                            <input
                                type="tel"
                                name="phone"
                                className={`form-control ${hasError('phone') ? 'is-invalid' : ''}`}
                                required
                                placeholder="Mobile Number"
                                value={values.phone}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                maxLength="14"
                            />
                            {hasError('phone') && (
                                <div className="invalid-feedback d-block">
                                    {errors.phone}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* City Field */}
                    <div className="col-lg-6 col-md-12">
                        <div className="form-group">
                            <input
                                type="text"
                                name="city"
                                className={`form-control ${hasError('city') ? 'is-invalid' : ''}`}
                                required
                                placeholder="City"
                                value={values.city}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                maxLength="50"
                            />
                            {hasError('city') && (
                                <div className="invalid-feedback d-block">
                                    {errors.city}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Message Field */}
                    <div className="col-lg-12 col-md-12">
                        <div className="form-group">
                            <textarea
                                name="message"
                                className={`form-control ${hasError('message') ? 'is-invalid' : ''}`}
                                cols="30"
                                rows="6"
                                required
                                placeholder="Describe your Requirement"
                                value={values.message}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                maxLength="500"
                            />
                            {hasError('message') && (
                                <div className="invalid-feedback d-block">
                                    {errors.message}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="col-lg-12 col-md-12">
                        <button
                            type="submit"
                            className="default-btn"
                            disabled={submitting}
                        >
                            {submitting ? "Submitting..." : "Submit"}
                            <span className="default-btn-hover"></span>
                            <i className="bx bx-right-arrow-alt"></i>
                        </button>
                    </div>
                </div>
            </form>

            {/* Success Message */}
            {success && (
                <div className="alert alert-success mt-4">
                    <i className="bx bx-check-circle"></i> Thanks! We'll reach out soon.
                </div>
            )}

            {/* General Error Message */}
            {error && (
                <div className="alert alert-danger mt-4">
                    <i className="bx bx-error-circle"></i> {error}
                </div>
            )}
        </>
    )
}

export default Form